import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import type { SessionUser, UserRole } from '@ns-b2b/shared';

interface AuthContextType {
    user: SessionUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (userId: string, role?: UserRole) => Promise<void>;
    logout: () => Promise<void>;
    refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<SessionUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check session on mount
    useEffect(() => {
        checkSession();
        handleHandoffToken();
    }, []);

    // Check for handoff token in URL (Safari ITP workaround)
    const handleHandoffToken = async () => {
        const params = new URLSearchParams(window.location.search);
        const handoffToken = params.get('handoff_token');

        if (handoffToken) {
            try {
                const response = await fetch('/api/auth/line/handoff', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: handoffToken }),
                    credentials: 'include',
                });

                if (response.ok) {
                    const data = await response.json();
                    setUser(data.user);
                }
            } catch (error) {
                console.error('Handoff failed:', error);
            }

            // Clean URL
            const url = new URL(window.location.href);
            url.searchParams.delete('handoff_token');
            window.history.replaceState({}, '', url.toString());
        }
    };

    const checkSession = async () => {
        try {
            const response = await fetch('/api/auth/session', {
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                if (data.authenticated && data.user) {
                    setUser(data.user);
                }
            }
        } catch (error) {
            console.error('Session check failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const login = useCallback(async (userId: string, role?: UserRole) => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/auth/mock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, role }),
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
            } else {
                throw new Error('Login failed');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
        } finally {
            setUser(null);
        }
    }, []);

    const refreshSession = useCallback(async () => {
        await checkSession();
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading,
                isAuthenticated: !!user,
                login,
                logout,
                refreshSession,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
